import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod";
import { SiteShell } from "@/components/site/SiteShell";
import { ResourcesIndex } from "@/components/resources/ResourcesIndex";
import { alternatePath, SITE_URL } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { publicResourcesQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import { withStaticResources } from "@/content/static-resources";
import type { EditorialResource } from "@/components/resources/ResourceEditorialPrimitives";

const recursosSearchSchema = z.object({
  q: z.string().catch("").default(""),
  cat: z
    .enum(["all", "performance", "conversion", "technical-seo", "migration", "shopify-development"])
    .catch("all")
    .default("all"),
  page: z.coerce.number().int().min(1).max(50).catch(1).default(1),
});

export const Route = createFileRoute("/es/recursos/")({
  validateSearch: recursosSearchSchema,
  search: { middlewares: [stripSearchParams({ q: "", cat: "all", page: 1 })] },
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    const data = await context.queryClient.ensureQueryData(publicResourcesQuery());
    return {
      ...data,
      resources: withStaticResources(((data?.resources ?? []) as EditorialResource[])),
    };
  },
  head: ({ loaderData }) => {
    const resources = ((loaderData as any)?.resources ?? []) as any[];
    return buildHead({
      locale: "es",
      title: "Recursos y playbooks de Shopify · Shugert",
      description:
        "Guías Shopify probadas en producción: performance, CRO, SEO técnico, migraciones y desarrollo, del equipo que las implementa.",
      path: "/es/recursos",
      altPath: alternatePath("/es/recursos", "en"),
      feeds: [{ href: "/es/recursos/rss.xml", title: "Shugert Recursos" }],
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Recursos", path: "/es/recursos" },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Recursos Shugert",
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          numberOfItems: resources.length,
          itemListElement: resources.map((r: any, i: number) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/es/recursos/${r.slug_es ?? r.slug}`,
            name: r.title_es || r.title_en || r.slug,
          })),
        },
      ],
    });
  },
  component: () => {
    const data = Route.useLoaderData();
    return (
      <SiteShell>
        <ResourcesIndex locale="es" resources={data?.resources ?? []} />
      </SiteShell>
    );
  },
});